/* eslint-disable max-len */
import React from 'react';
import Typography from 'housecall-ui/dist/DesignSystem/DataDisplay/Typography/Typography';
import Video from 'housecall-ui/dist/DesignSystem/DataDisplay/Video/Video';
import Grid from 'housecall-ui/dist/DesignSystem/Layout/Grid/Grid';
import Link from 'housecall-ui/dist/DesignSystem/Navigation/Link/Link';
import VehicleGpsTrackingAppIcon from 'housecall-ui/dist/IconsAndImages/AppIcons/VehicleGpsTrackingAppIcon';

import Button from '../VehicleGpsTracking/Button';

const VehicleGpsTrackingStrategy = {
  previewInfo: {
    name: 'Vehicle GPS Tracking',
    iconSvg: <VehicleGpsTrackingAppIcon variant="duo" size="jumbo" />,
    newTab: true,
  },
  headerInfo: {
    name: 'Vehicle GPS Tracking',
    category: 'Fleet management',
    details: (
      <Grid container spacing={5}>
        <Grid item xs={12}>
          <Typography paragraph>
            Improve your dispatching efficiency and provide 5-star customer service with Vehicle GPS Tracking from
            Housecall Pro. With easy-to-install GPS hardware that plugs right into your trucks, you can track your
            vehicles in real time on the HCP map and automatically display vehicle locations in On My Way texts to
            customers.
          </Typography>
          <Typography paragraph>
            Vehicle GPS Tracking includes full access to Force by Mojio’s Fleet Management dashboard. With insights on
            vehicle health, vehicle status, trip history, driver safety, and more, intelligently managing your vehicle
            fleet has never been easier.
          </Typography>
          <Typography paragraph>
            Combining Housecall Pro with Force by Mojio boosts your customer experience, improves your operations and
            provides better control over your vehicle related costs. Ready to get started? Get up and running fast with
            simple all-in pricing, no contracts, and free shipping. See{' '}
            <Link href="http://www.housecallpro.com/features/vehicle-gps-tracking/">our website</Link> and{' '}
            <Link href="https://help.housecallpro.com/en/collections/3401160-vehicle-gps-tracking">Help Center</Link>{' '}
            for more information.
          </Typography>

          <Typography paragraph>Note: Force by Mojio is currently available in the US only.</Typography>
        </Grid>
        <Grid item xs={12}>
          <h4>What’s included:</h4>
          <ul>
            <li>
              Universal (OBD2) GPS hardware devices fulfilled by Force by Mojio.
              <ul>
                <li>
                  <b>
                    If you cancel any device, you will have 30 days upon canceling to return the device to Mojio. Each
                    unreturned device will incur a $150 charge.
                  </b>{' '}
                  <Link href="https://help.housecallpro.com/en/articles/6613406-vehicle-gps-tracking-downgrade-process">
                    Learn more
                  </Link>
                </li>
              </ul>
            </li>
            <li>Accurate, real-time locations on the Housecall Pro Map</li>
            <li>Automatic technician locations via On My Way texts to customers</li>
            <li>
              Fleet Management dashboard from Force by Mojio, which includes: accurate trip history, geo-fences, driver
              scores, disturbance alerts, fleet reports, and more
            </li>
          </ul>
        </Grid>
        <Grid item xs={12}>
          <h4>Before you start:</h4>
          <ul>
            <li>Set up is seamless with easy installation, no contracts, and no shipping fees.</li>
            <li>
              Each truck will have 1 assigned driver. This feature works best if you don’t swap drivers regularly
              (occasional updates are fine!)
            </li>
            <li>
              After purchase, you’ll receive important setup instructions from Housecall Pro and Force by Mojio in your
              email.
            </li>
            <li>
              You’re not done yet! Return to this app page to enter your shipping address. You will not receive your
              devices until you do.
            </li>
            <li>
              Once Force by Mojio has received your shipping details, a magic login link will appear on this page,
              giving you access to the Force by Mojio dashboard. Login to assign your vehicle drivers and complete your
              setup.
            </li>
          </ul>
        </Grid>
        <Grid item xs={12}>
          <h4>Force by Mojio Mobile App</h4>
          <Typography paragraph>
            If you’d like to access your Fleet Management Dashboard from the field, you may download the Force by Mojio
            app:
          </Typography>
          <ol>
            <li>Search for Force by Mojio in your iPhone or Android app store, and download the app</li>
            <li>Open the app and click &quot;Reset Password&quot;</li>
            <li>Enter the email address associated with your Housecall Pro account</li>
            <li>Follow the prompts to set a new password and access your Force by Mojio account</li>
          </ol>
        </Grid>
        <Grid item xs={12}>
          <h4>Why Force by Mojio:</h4>
          <Typography paragraph>
            Housecall Pro has partnered with Force by Mojio because we share the common value to “Champion the Pro”.
            Force by Mojio is trusted by over 1 million vehicles worldwide. Additionally, Force by Mojio meets and
            exceeds the stringent reliability requirements of major phone carriers, car manufacturers, and our own high
            technical standards by connecting more than one million trucks, vans and cars to their platform today.
          </Typography>
          <Video src="https://fast.wistia.net/embed/iframe/tm1ds120qf?autoPlay=false" />
        </Grid>
      </Grid>
    ),
    iconSvg: <VehicleGpsTrackingAppIcon variant="duo" size="jumbo" />,
  },
  media: {},
  sellingPoints: {},
  price: {
    title: 'Housecall Pro Add-on Price',
    hideToggle: true,
    appActionButton: <Button />,
    description: (
      <Grid container spacing={8}>
        <Grid item xs={4}>
          <Typography align="left" variant="body1">
            Basic plan
          </Typography>
        </Grid>
        <Grid item xs={8}>
          <Typography align="right" variant="body1">
            + $20/month per vehicle
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography align="left" variant="body1">
            Essentials plan
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography align="right" variant="body1">
            + $20/month per vehicle
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography align="left" variant="body1">
            MAX plan
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography align="right" variant="body1">
            + $20/month per vehicle
          </Typography>
        </Grid>
      </Grid>
    ),
  },
  planAvailability: {
    title: 'Housecall Pro Plan',
    description: (
      <Grid container spacing={8}>
        <Grid item xs={4}>
          <Typography align="left" variant="body1">
            Basic plan
          </Typography>
        </Grid>
        <Grid item xs={8}>
          <Typography align="right" variant="body1">
            Available
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography align="left" variant="body1">
            Essentials plan
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography align="right" variant="body1">
            Available
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography align="left" variant="body1">
            MAX plan
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography align="right" variant="body1">
            Available
          </Typography>
        </Grid>
      </Grid>
    ),
  },
  supportContacts: [
    { type: 'Housecall Pro', color: 'normal', variant: 'body1' },
    { type: 'Phone', contact: '(866) 696-1069' },
    {
      type: 'Support',
      contact: (
        <Link href="https://help.housecallpro.com/en/articles/3557902-using-the-blue-chat-bubble">
          Blue Chat Bubble
        </Link>
      ),
    },
    { type: 'Help Center', contact: <Link href="https://help.housecallpro.com">help.housecallpro.com</Link> },
    { type: 'divider' },
    { type: 'Force by Mojio', color: 'normal', variant: 'body1' },
    { type: 'Help Center', contact: <Link href="https://help.forcebymojio.com">help.forcebymojio.com</Link> },
  ],
};

export default VehicleGpsTrackingStrategy;
